import { useLayoutEffect } from "react";
import { captureAppThemeColor, useDawnState } from "./dawnStore";
import "./dawn.css";

/**
 * The Morgengrauen overlay (spec §5.2): mounted once under the router, above the keyed theme
 * provider, so it survives the route switch. It only mirrors the dawn store: nothing while
 * idle, the two layers (twilight, horizon) while preparing or running, and `<html data-dawn>`
 * while running (the attribute scopes the view-transition names and keyframes in dawn.css).
 * The daylight is the new page itself, uncovered by the overlay's rising edge.
 */
export function DawnHost(): JSX.Element | null {
  const { phase, mode, fallback } = useDawnState();
  const dawnMode = mode ?? "day";
  const running = phase === "running";

  // DawnHost mounts with the app, before any routed page: the app's own theme-color is still there.
  useLayoutEffect(() => {
    captureAppThemeColor(document);
  }, []);

  useLayoutEffect(() => {
    if (!running) return;
    const root = document.documentElement;
    root.dataset.dawn = dawnMode;
    return () => {
      delete root.dataset.dawn;
    };
  }, [running, dawnMode]);

  if (phase === "idle") return null;

  return (
    <div
      data-dawn-overlay=""
      aria-hidden="true"
      className="dawn-overlay"
      data-mode={dawnMode}
      data-fallback={fallback ? "" : undefined}
    >
      <div className="dawn-overlay__twilight" />
      <div className="dawn-overlay__horizon" />
    </div>
  );
}
